import z from "zod"
import { School, schoolSchemas } from "@/models"

const DistrictAppConfigurationSchema = z.object({
  districtAcronym: z.string().transform(s => s === '' ? undefined : s).optional(),
  logoUrl: z.string().transform(s => s === '' ? undefined : s).optional(),
  smallLogoUrl: z.string().transform(s => s === '' ? undefined : s).optional(),
})

export class DistrictAppConfiguration {
  constructor(data: z.infer<typeof DistrictAppConfigurationSchema>) {
    Object.assign(this, data)
  }
  public readonly districtAcronym?: string
  public readonly logoUrl?: string
  public readonly smallLogoUrl?: string
}

const DistrictCMSDataSchema = z.object({
  appConfiguration: DistrictAppConfigurationSchema,
})

export class DistrictCMSData {
  constructor(data: z.infer<typeof DistrictCMSDataSchema>) {
    this.appConfiguration = new DistrictAppConfiguration(data.appConfiguration)
  }
  public readonly appConfiguration!: DistrictAppConfiguration
}

const DistrictSchema = z.object({
  districtId: z.number(),
  cmsDistrictId: z.number().optional(),
  orgId: z.number().transform(i => i === 0 ? undefined : i).optional(),
  siteId: z.number().transform(i => i === 0 ? undefined : i).optional(),
  feedId: z.number().transform(i => i === 0 ? undefined : i).optional(),
  name: z.string(),
  shortName: z.string().optional(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().optional(),
  districtCode: z.string().optional(),
  facebookPage: z.string().optional(),
  twitterHandle: z.string().optional(),
  cmsData: DistrictCMSDataSchema.optional(),
  schools: z.array(schoolSchemas.School)
})

export class District {
  constructor(data: z.infer<typeof DistrictSchema>) {
    Object.assign(this, data)
    this.cmsData = data.cmsData ? new DistrictCMSData(data.cmsData) : undefined
    this.schools = data.schools.map(school => new School(school))
  }

  public readonly districtId!: number
  public readonly cmsDistrictId?: number
  public readonly orgId?: number
  public readonly siteId?: number
  public readonly feedId?: number
  public readonly name!: string
  public readonly shortName?: string
  public readonly address!: string
  public readonly city!: string
  public readonly state!: string
  public readonly zip!: string
  public readonly phone?: string
  public readonly districtCode?: string
  public readonly facebookPage?: string
  public readonly twitterHandle?: string
  public readonly cmsData?: DistrictCMSData
  public readonly schools!: School[]

  public logo() {
    return this.cmsData?.appConfiguration.logoUrl
  }
}

export class Districts {
  public static GetBannerLogo(districts: District[]) {
    // Prefer district logos
    for (const district of districts) {
      const logo = district.logo()

      if (logo) return logo
    }

    // Fallback to school logo
    for (const district of districts) {
      for (const school of district.schools) {
        const logo = school.logo()

        if (logo) return logo
      }
    }

    return 'https://schoolmanager.s3.amazonaws.com/uploads/pictures/caef8bc4c5d04073941cd141f1028201.png'
  }
}

export const districtSchemas = {
  DistrictAppConfiguration: DistrictAppConfigurationSchema,
  DistrictCMSData: DistrictCMSDataSchema,
  District: DistrictSchema
}
